/**
 * 인라인 미니 달력 (MY일정 일간·주간 좌측 — 레거시 jQuery UI inline datepicker 미러, 2026-09-22)
 *
 * 항상 펼쳐진 월 달력. 날짜 클릭 = 즉시 onSelect(dateStr) (레거시 onSelect → search() 미러, 확인 단계 없음).
 * 공휴일은 window.LDK_HOLIDAYS[yyyy][yyyymmdd] = 명칭 (static/js/holidays.js — 페이지에서 먼저 로드).
 * 마크업은 view/myschedule/fragments/common.html :: miniCalendar 가 x-for 로 그린다(플러그인은 상태·계산만).
 *
 * 사용법:
 *   <div x-data="miniCalendar({ selected: '2026-09-22', onSelect: (d) => moveScheduleDate(d) })">
 *
 * 옵션:
 *   - selected   : 선택(표시 중인) 날짜 'YYYY-MM-DD' — 달력은 이 달을 연다
 *   - rangeStart / rangeEnd : 주간용 강조 범위 'YYYY-MM-DD' (선택 없이 범위만 칠할 때)
 *   - onSelect   : (dateStr) => {} 날짜 클릭 콜백
 */
export default (options = {}) => ({
    options: {
        selected: '',
        rangeStart: '',
        rangeEnd: '',
        onSelect: null,
        ...options
    },

    year: 0,
    month: 0,          // 0~11
    cells: [],         // 그 달에 필요한 주 수 × 7일(28~42 셀)
    dayNames: ['일', '월', '화', '수', '목', '금', '토'],

    init() {
        // th:attr 은 빈 문자열 속성을 생략하므로 dataset 값이 undefined 로 들어올 수 있다 — 빈 문자열로 정규화
        this.options.selected = this.options.selected || '';
        this.options.rangeStart = this.options.rangeStart || '';
        this.options.rangeEnd = this.options.rangeEnd || '';
        const base = this._parse(this.options.selected) || this._parse(this.options.rangeStart) || new Date();
        this.year = base.getFullYear();
        this.month = base.getMonth();
        this.build();
    },

    get title() {
        return `${this.year}년 ${this.month + 1}월`;
    },

    /** 표시 중인 달이 오늘이 속한 달인지 — [오늘] 버튼 강조용 */
    get isCurrentMonth() {
        const t = new Date();
        return t.getFullYear() === this.year && t.getMonth() === this.month;
    },

    build() {
        const first = new Date(this.year, this.month, 1);
        const start = new Date(this.year, this.month, 1 - first.getDay());   // 일요일 시작(레거시 동일)
        const todayStr = this._fmt(new Date());
        const sel = this.options.selected || '';
        const rs = this.options.rangeStart || '';
        const re = this.options.rangeEnd || '';
        const H = window.LDK_HOLIDAYS || {};

        // 행 수는 그 달에 필요한 만큼만(4~6주) — 고정 6주는 세로가 길어져 폐기(2026-09-22 사용자, 레거시 datepicker 동일)
        const daysInMonth = new Date(this.year, this.month + 1, 0).getDate();
        const weeks = Math.ceil((first.getDay() + daysInMonth) / 7);
        const cells = [];
        for (let i = 0; i < weeks * 7; i++) {
            const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
            const dateStr = this._fmt(d);
            const code = dateStr.replace(/-/g, '');
            const y = code.substring(0, 4);
            const holiday = H[y] && H[y][code] ? String(H[y][code]).trim() : '';
            const dow = d.getDay();
            cells.push({
                dateStr,
                day: d.getDate(),
                dow,
                inMonth: d.getMonth() === this.month,
                isToday: dateStr === todayStr,
                isSelected: sel !== '' && dateStr === sel,
                inRange: rs !== '' && re !== '' && dateStr >= rs && dateStr <= re,
                holiday,
                // 색 — 일요일·공휴일 빨강 > 토요일 파랑 > 기본 (레거시 nationalDays 미러)
                tone: (dow === 0 || holiday) ? 'red' : (dow === 6 ? 'blue' : '')
            });
        }
        this.cells = cells;
    },

    prev() {
        if (this.month === 0) { this.year--; this.month = 11; } else { this.month--; }
        this.build();
    },

    next() {
        if (this.month === 11) { this.year++; this.month = 0; } else { this.month++; }
        this.build();
    },

    /** 오늘이 속한 달로 이동만(선택은 클릭으로) */
    goToday() {
        const t = new Date();
        this.year = t.getFullYear();
        this.month = t.getMonth();
        this.build();
    },

    select(cell) {
        if (typeof this.options.onSelect === 'function') {
            this.options.onSelect(cell.dateStr);
        }
    },

    _parse(s) {
        if (!s) return null;
        const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
        if (!m) return null;
        return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    },

    _fmt(d) {
        const mm = ('0' + (d.getMonth() + 1)).slice(-2);
        const dd = ('0' + d.getDate()).slice(-2);
        return `${d.getFullYear()}-${mm}-${dd}`;
    }
});
